import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import { Box, Button, CircularProgress, FormHelperText, TextField, Typography, Divider } from '@mui/material';
import { useThemeColors } from '../../../hooks/useThemeColors';
import { useAtom } from 'jotai';
import dayjs from 'dayjs';
import {
  UseCreateDriverManager,
  useEditDriverManager,
} from '../../Query/DriverManagerQuery';
import { deliveryBoyAtom } from './ManageDriverTable';
import LocationSearchInput from '../../Stateless/LocationSearchInput';
import { useState } from 'react';
import { COLORS } from '../../../theme';
import PhoneNumberInput from '../Utils/PhoneNumberInput';
import { MultiSelectField } from '../Reports/MultiSelectField';
import ImageUploadField from '../Utils/ImageUploadField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

export const deliveryVehicleOptions = [
  { value: 'Truck', label: 'Truck' },
  { value: 'Two-wheeler', label: 'Two-wheeler' },
];

// Component to render vehicle input fields dynamically
const VehicleInputFields: React.FC<{ 
  form: any; 
  selectedVehicleTypes: string[];
  country: string;
}> = ({ form, selectedVehicleTypes }) => {
  // Initialize vehicles array if not exists
  if (!form.values.vehicles || !Array.isArray(form.values.vehicles)) {
    form.setFieldValue('vehicles', []);
  }

  // Ensure vehicles array matches selected types
  const vehicles = form.values.vehicles || [];
  const updatedVehicles = selectedVehicleTypes.map((vehicleType) => {
    const existingVehicle = vehicles.find((v: any) => v.vehicleType === vehicleType);
    return existingVehicle || {
      vehicleType,
      vehicleName: '',
      vehilceType: '',
      vehicleNumber: '',
    };
  });

  // Update form values if vehicles array changed
  if (JSON.stringify(vehicles) !== JSON.stringify(updatedVehicles)) {
    form.setFieldValue('vehicles', updatedVehicles);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
      {selectedVehicleTypes.map((vehicleType, index) => {
        const vehicle = updatedVehicles[index];
        const vehicleIndex = index;
        
        const vehicleNameError = form.touched.vehicles?.[vehicleIndex]?.vehicleName &&
          Boolean((form.errors.vehicles as any)?.[vehicleIndex]?.vehicleName);
        const vehicleModelError = form.touched.vehicles?.[vehicleIndex]?.vehilceType &&
          Boolean((form.errors.vehicles as any)?.[vehicleIndex]?.vehilceType);
        const vehicleNumberError = form.touched.vehicles?.[vehicleIndex]?.vehicleNumber &&
          Boolean((form.errors.vehicles as any)?.[vehicleIndex]?.vehicleNumber);
        
        return (
          <Box key={vehicleType} sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 2, 
            p: 3,
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
            transition: 'box-shadow 0.2s ease',
            '&:hover': {
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)',
            }
          }}>
            <Typography variant="h6" sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: '#333',
              fontSize: '18px',
              letterSpacing: '0.02em'
            }}>
              {vehicleType} Details
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Vehicle Name"
                placeholder="Enter vehicle name"
                value={vehicle?.vehicleName || ''}
                onChange={(e) => {
                  const newVehicles = [...updatedVehicles];
                  newVehicles[vehicleIndex] = {
                    ...newVehicles[vehicleIndex],
                    vehicleName: e.target.value,
                  };
                  form.setFieldValue('vehicles', newVehicles);
                  form.setFieldTouched(`vehicles.${vehicleIndex}.vehicleName`, true);
                }}
                onBlur={() => {
                  form.setFieldTouched(`vehicles.${vehicleIndex}.vehicleName`, true);
                }}
                error={vehicleNameError}
                helperText={
                  vehicleNameError
                    ? (form.errors.vehicles as any)?.[vehicleIndex]?.vehicleName
                    : ''
                }
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: vehicleNameError ? COLORS.error.main : COLORS.text.label,
                    fontWeight: 500,
                    '& .MuiInputLabel-asterisk': {
                      color: COLORS.error.main,
                    },
                  },
                }}
                sx={{ 
                  maxWidth: 400,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fafafa',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                    '& fieldset': {
                      borderColor: vehicleNameError ? COLORS.error.main : '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: vehicleNameError ? COLORS.error.main : '#bdbdbd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: vehicleNameError ? COLORS.error.main : COLORS.primary.main,
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    opacity: '1 !important',
                    color: 'rgba(0, 0, 0, 0.5)',
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                    marginTop: '4px',
                    fontSize: '12px',
                  },
                }}
              />
              <TextField
                label="Vehicle Model"
                placeholder="Enter vehicle model"
                value={vehicle?.vehilceType || ''}
                onChange={(e) => {
                  const newVehicles = [...updatedVehicles];
                  newVehicles[vehicleIndex] = {
                    ...newVehicles[vehicleIndex],
                    vehilceType: e.target.value,
                  };
                  form.setFieldValue('vehicles', newVehicles);
                  form.setFieldTouched(`vehicles.${vehicleIndex}.vehilceType`, true);
                }}
                onBlur={() => {
                  form.setFieldTouched(`vehicles.${vehicleIndex}.vehilceType`, true);
                }}
                error={vehicleModelError}
                helperText={
                  vehicleModelError
                    ? (form.errors.vehicles as any)?.[vehicleIndex]?.vehilceType
                    : ''
                }
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: vehicleModelError ? COLORS.error.main : COLORS.text.label,
                    fontWeight: 500,
                    '& .MuiInputLabel-asterisk': {
                      color: COLORS.error.main,
                    },
                  },
                }}
                sx={{ 
                  maxWidth: 400,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fafafa',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                    '& fieldset': {
                      borderColor: vehicleModelError ? COLORS.error.main : '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: vehicleModelError ? COLORS.error.main : '#bdbdbd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: vehicleModelError ? COLORS.error.main : COLORS.primary.main,
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    opacity: '1 !important',
                    color: 'rgba(0, 0, 0, 0.5)',
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                    marginTop: '4px',
                    fontSize: '12px',
                  },
                }}
              />
              <TextField
                label="Vehicle Number"
                placeholder="Enter vehicle number"
                value={vehicle?.vehicleNumber || ''}
                onChange={(e) => {
                  const newVehicles = [...updatedVehicles];
                  newVehicles[vehicleIndex] = {
                    ...newVehicles[vehicleIndex],
                    vehicleNumber: e.target.value,
                  };
                  form.setFieldValue('vehicles', newVehicles);
                  form.setFieldTouched(`vehicles.${vehicleIndex}.vehicleNumber`, true);
                }}
                onBlur={() => {
                  form.setFieldTouched(`vehicles.${vehicleIndex}.vehicleNumber`, true);
                  // Trigger validation by validating the form
                  form.validateField(`vehicles.${vehicleIndex}.vehicleNumber`);
                }}
                error={vehicleNumberError}
                helperText={
                  vehicleNumberError
                    ? (form.errors.vehicles as any)?.[vehicleIndex]?.vehicleNumber
                    : ''
                }
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: vehicleNumberError ? COLORS.error.main : COLORS.text.label,
                    fontWeight: 500,
                    '& .MuiInputLabel-asterisk': {
                      color: COLORS.error.main,
                    },
                  },
                }}
                sx={{ 
                  maxWidth: 400,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fafafa',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#ffffff',
                    },
                    '& fieldset': {
                      borderColor: vehicleNumberError ? COLORS.error.main : '#e0e0e0',
                    },
                    '&:hover fieldset': {
                      borderColor: vehicleNumberError ? COLORS.error.main : '#bdbdbd',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: vehicleNumberError ? COLORS.error.main : COLORS.primary.main,
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    opacity: '1 !important',
                    color: 'rgba(0, 0, 0, 0.5)',
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0,
                    marginTop: '4px',
                    fontSize: '12px',
                  },
                }}
              />
            </Box>
            {index < selectedVehicleTypes.length - 1 && (
              <Divider sx={{ mt: 3, mb: 1 }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// Utility to split phone number into country code and phone
const COUNTRY_CODES = [
  '+971', '+966', '+965', '+973', '+974', '+968', '+20', '+962', '+961', '+964', '+92', '+91'
];
function splitPhoneNumber(fullNumber: string) {
  if (!fullNumber) return { countryCode: '', phone: '' };
  const code = COUNTRY_CODES.sort((a, b) => b.length - a.length)
    .find(code => fullNumber.startsWith(code));
  if (code) {
    return {
      countryCode: code,
      phone: fullNumber.slice(code.length)
    };
  }
  return { countryCode: '', phone: fullNumber };
}

// Vehicle number validation patterns by country
const VEHICLE_NUMBER_PATTERNS = {
  'Jordan': { // +962
    // pattern: /^(?:(?:1[0-9]|2[0-9]|3[0-5])|(?:PM|SN|PR|JC|GV|AQ|CD|TM))-[0-9]{4,5}$/,
    pattern: /^[0-9]{1,}-[0-9]{1,}$/,
    message: 'Enter a valid Jordan vehicle number (e.g., 12-3456 or PM-12345)'
  },
  'United Arab Emirates': { // +971
    pattern: /^[A-Z]{1,3}[0-9]{1,4}$/,
    message: 'Enter a valid UAE vehicle number (e.g., A123 or ABC1234)'
  },
  'Saudi Arabia': { // +966
    pattern: /^[0-9]{4,6}$/,
    message: 'Enter a valid Saudi vehicle number (4-6 digits)'
  },
  'Kuwait': { // +965
    pattern: /^[0-9]{5,6}$/,
    message: 'Enter a valid Kuwait vehicle number (5-6 digits)'
  },
  'Bahrain': { // +973
    pattern: /^[0-9]{4,5}$|^[0-9]{2}-[0-9]{4}$/,
    message: 'Enter a valid Bahrain vehicle number (e.g., 1234, 12345, or 12-3456)'
  },
  'Qatar': { // +974
    pattern: /^[0-9]{4,6}$/,
    message: 'Enter a valid Qatar vehicle number (4-6 digits)'
  },
  'Oman': { // +968
    pattern: /^[0-9]{4,5}$/,
    message: 'Enter a valid Oman vehicle number (4-5 digits)'
  },
  'Egypt': { // +20
    pattern: /^[0-9]{6,7}$/,
    message: 'Enter a valid Egypt vehicle number (6-7 digits)'
  },
  'Lebanon': { // +961
    pattern: /^[0-9]{4,6}$/,
    message: 'Enter a valid Lebanon vehicle number (4-6 digits)'
  },
  'Iraq': { // +964
    pattern: /^[0-9]{5,7}$/,
    message: 'Enter a valid Iraq vehicle number (5-7 digits)'
  },
  'Pakistan': { // +92
    pattern: /^[A-Z]{3}[0-9]{3,4}$/,
    message: 'Enter a valid Pakistan vehicle number (e.g., ABC123 or ABC1234)'
  },
  'India': { // +91
    pattern: /^[A-Z]{2}[0-9]{1,2}[A-Z]{1,2}[0-9]{4}$/,
    message: 'Enter a valid India vehicle number (e.g., KA01AB1234 or DL1A1234)'
  }
};

const AddDriver = ({ onCancel }: { onCancel: () => void }) => {
  const [inputKey, setInputKey] = useState(0);
  const [deliveryBoyData] = useAtom(deliveryBoyAtom);
  const { warning } = useThemeColors();

  // Use splitPhoneNumber utility for both numbers
  const { countryCode, phone } = splitPhoneNumber(deliveryBoyData?.mobileNumber || '');
  const { countryCode: altCountryCode, phone: altPhone } = splitPhoneNumber(deliveryBoyData?.alternativeMobileNumber || '');

  const { mutate: CreateDriverManager, isPending: CreateDriverManagerLoading } =
    UseCreateDriverManager();

  const { mutate: EditDriverManager, isPending: EditDriverManagerLoading } =
    useEditDriverManager();

  const [address, setAddress] = useState(deliveryBoyData ? deliveryBoyData?.location : '');
  const nameRegex = /^[A-Za-z0-9 ]+$/;
  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required('Please enter First Name ')
      .matches(/^[A-Za-z\s]+$/, 'First Name should only contain letters and spaces')
      .max(100, 'First Name cannot exceed 50 characters')
      .test(
        'not-only-spaces',
        'First Name cannot be only spaces',
        (value) => {
          if (typeof value !== 'string') return false;
          return value.trim().length > 0;
        }
      ),
    lastName: Yup.string()
      .required('Please enter Last Name ')
      .matches(/^[A-Za-z\s]+$/, 'First Name should only contain letters and spaces')
      .max(100, 'Last Name cannot exceed 50 characters')
      .test(
        'not-only-spaces',
        'Last Name cannot be only spaces',
        (value) => {
          if (typeof value !== 'string') return false;
          return value.trim().length > 0;
        }
      ),
    email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/,
        'Please enter a valid email address'
      )
      .required('Email is required'),
    mobileNumber: Yup.string()
      .required('Please enter the mobile number')
      .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
      .test(
        'len',
        'Mobile number must be 9 digits or more',
        (val) => val?.length >= 9
      )
      .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
    alternativeMobileNumber: Yup.string()
      .nullable()
      .test(
        'alt-mobile-format',
        'Mobile number must be 9 digits or more',
        (val) => {
          if (!val) return true; // Optional field
          if (!/^[0-9]+$/.test(val)) return false;
          return val.length >= 9;
        }
      )
      .test(
        'alt-mobile-not-zero-or-same',
        'Alternate number must be different from primary and not all zeroes',
        function (val) {
          if (!val) return true; // Optional field
          const primary = this.parent.mobileNumber;
          if (val === '0000000000') return false;
          if (primary && val === primary) return false;
          return true;
        }
      ),
    deliveryVehicleType: Yup.array()
      .of(Yup.string().oneOf(['Truck', 'Two-wheeler']))
      .min(1, 'Please select at least one vehicle type')
      .required('Delivery vehicle type is required'),
    drivingLicenceNumber: Yup.string()
      .required('Driving licence number is required')
      .max(50, 'Driving licence number cannot exceed 50 characters')
      .test(
        'not-only-spaces',
        'Driving licence number cannot be only spaces',
        (value) => {
          if (typeof value !== 'string') return false;
          return value.trim().length > 0;
        }
      ),
    driverLicenceProof: Yup.string()
      .required('Driving licence proof document is required'),
    drivingLicenceExpiryDate: Yup.string()
      .required('Driving licence expiry date is required')
      .test(
        'future-date',
        'Expiry date must be a future date',
        (value) => {
          if (!value) return false;
          const expiryDate = dayjs(value);
          const today = dayjs().startOf('day');
          return expiryDate.isAfter(today) || expiryDate.isSame(today);
        }
      ),
    vehicles: Yup.array()
      .of(
        Yup.object().shape({
          vehicleType: Yup.string().required(),
          vehicleName: Yup.string()
            .required('Please enter the vehicle name')
            .max(100, 'Name must be at most 100 characters')
            .test('no-whitespace', 'Vehicle name cannot be empty or just spaces', (value) =>
              value?.trim().length > 0
            )
            .matches(nameRegex, 'Vehicle name should not contain special characters'),
          vehilceType: Yup.string()
            .required('Please enter the vehicle model')
            .max(100, 'Model must be at most 100 characters')
            .test('no-whitespace', 'Vehicle type cannot be empty or just spaces', (value) =>
              value?.trim().length > 0
      )
      .matches(nameRegex, 'Vehicle type should not contain special characters'),
    vehicleNumber: Yup.string()
      .min(1, 'Please provide vehicle details for all selected types')
      .required('Vehicle number is required')
      .test('country-specific-validation', '', function(vehicles) {
        // const { country } = this.parent;
        const country  = 'Jordan';
        if (!vehicles || !Array.isArray(vehicles)) return true; // Let required validation handle empty values
        for (let i = 0; i < vehicles.length; i++) {
          const vehicle = vehicles[i];
          if (!vehicle?.vehicleNumber) continue;
          
          const pattern = VEHICLE_NUMBER_PATTERNS[country as keyof typeof VEHICLE_NUMBER_PATTERNS];
          if (!pattern) {
            if (!/^[0-9]{1,}-[0-9]{1,}$/.test(vehicle.vehicleNumber)) {
              return this.createError({ 
                path: `vehicles[${i}].vehicleNumber`,
                message: `Vehicle ${i + 1}: Enter a valid vehicle number (3-10 alphanumeric characters)` 
              });
            }
            continue;
          }
          
          if (!pattern.pattern.test(vehicle.vehicleNumber)) {
            return this.createError({ 
              path: `vehicles[${i}].vehicleNumber`,
              message: `Vehicle ${i + 1}: ${pattern.message}` 
            });
            
          }
        }
        return true;
      })
        })
      ),
    maximumOrderLimit: Yup.number()
      .typeError('Maximum order limit must be a number')
      .required('Maximum order limit is required')
      .integer('Maximum order limit must be an integer')
      .min(1, 'Maximum order limit must be at least 1'),
    commonFare: Yup.number()
      .typeError('Common fare must be a number')
      .required('Common fare is required')
      .min(0, 'Common fare must be a positive number'),
    responseTime: Yup.number()
      .typeError('Response time must be a number')
      .required('Response time is required')
      .integer('Response time must be an integer')
      .min(0, 'Response time must be a positive number'),
    location: Yup.string().required('location is required')
      .test(
        'not-only-spaces',
        'Location cannot be only spaces',
        (value) => typeof value === 'string' && value.trim().length > 0
      ),

    country: Yup.string().required('Country is required'),

    state: Yup.string().required('State is required'),

    city: Yup.string().required('City is required'),
    ...(!deliveryBoyData && {
      password: Yup.string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .max(15, 'Password must not exceed 15 characters')
        .matches(/^\S+$/, 'Password must not contain spaces')
        .matches(/(?=.*[A-Z])/, 'Password must contain at least one uppercase letter')
        .matches(/(?=.*\d)/, 'Password must contain at least one number')
        .matches(/(?=.*[@$!%*?&#^()_+=\-{}[\]:;"'<>,.?/\\|`~])/, 'Password must contain at least one special character'),
    }),
  });
  return (
    <>
      {AllForm({
        initialValues: {
          firstName: deliveryBoyData ? deliveryBoyData?.firstName : '',
          lastName: deliveryBoyData ? deliveryBoyData?.lastName : '',
          email: deliveryBoyData ? deliveryBoyData?.email : '',
          countryCode: countryCode || '+962',
          mobileNumber: phone || '',
          alternativeCountryCode: altCountryCode || '+962',
          alternativeMobileNumber: altPhone || '',
          password: '',
          currencyCode: 'JOD',
          deliveryVehicleType: deliveryBoyData
            ? (Array.isArray(deliveryBoyData?.deliveryVehicleType) 
                ? deliveryBoyData.deliveryVehicleType 
                : deliveryBoyData.deliveryVehicleType 
                  ? [deliveryBoyData.deliveryVehicleType] 
                  : [])
            : [],
          vehicles: deliveryBoyData
            ? (Array.isArray((deliveryBoyData as any)?.vehicles) && (deliveryBoyData as any).vehicles.length > 0
                ? (deliveryBoyData as any).vehicles
                : deliveryBoyData.deliveryVehicleType
                  ? [{
                      vehicleType: deliveryBoyData.deliveryVehicleType,
                      vehicleName: deliveryBoyData.vehicleName || '',
                      vehilceType: deliveryBoyData.vehilceType || '',
                      vehicleNumber: deliveryBoyData.vehicleNumber || '',
                    }]
                  : [])
            : [],
          maximumOrderLimit: deliveryBoyData
            ? deliveryBoyData?.maximumOrderLimit
            : null,
          commonFare: deliveryBoyData
            ? (deliveryBoyData as any)?.commonFare || 0
            : 0,
          responseTime: deliveryBoyData
            ? (deliveryBoyData as any)?.responseTime || 0
            : 0,
          location: deliveryBoyData ? deliveryBoyData?.location : '',
          country: deliveryBoyData ? deliveryBoyData?.country : '',
          state: deliveryBoyData ? deliveryBoyData?.state : '',
          city: deliveryBoyData ? deliveryBoyData?.city : '',
          drivingLicenceNumber: deliveryBoyData ? deliveryBoyData?.drivingLicenceNumber || '' : '',
          driverLicenceProof: deliveryBoyData ? deliveryBoyData?.driverLicenceProof || '' : '',
          drivingLicenceExpiryDate: deliveryBoyData && deliveryBoyData?.drivingLicenceExpiryDate
            ? deliveryBoyData.drivingLicenceExpiryDate
            : '',
        },
        validationSchema,
        onSubmit: async (values: any) => {
          // Debug: log final validated form values before API call
          console.log('AddDriver onSubmit values:', values);
          // Handle vehicle data - use first vehicle for backward compatibility if API expects single vehicle
          const firstVehicle = values.vehicles && values.vehicles.length > 0 ? values.vehicles[0] : null;
          const deliveryVehicleTypeValue = Array.isArray(values.deliveryVehicleType) && values.deliveryVehicleType.length > 0
            ? values.deliveryVehicleType[0] // Use first selected type for backward compatibility
            : '';
          
          const modifiedValues = {
            ...values,
            currencyCode: 'JOD',
            maximumOrderLimit: +values.maximumOrderLimit,
            commonFare: +values.commonFare || 0,
            responseTime: +values.responseTime || 0,
            // Always send full primary mobile number with country code
            mobileNumber: `${values.countryCode}${values.mobileNumber}`,
            // Only send alternativeMobileNumber if user actually entered a number
            ...(values.alternativeMobileNumber
              ? {
                  alternativeMobileNumber: `${values.alternativeCountryCode}${values.alternativeMobileNumber}`,
                }
              : {}),
            // For backward compatibility, also include single vehicle fields
            deliveryVehicleType: deliveryVehicleTypeValue,
            vehicleName: firstVehicle?.vehicleName || '',
            vehilceType: firstVehicle?.vehilceType || '',
            vehicleNumber: firstVehicle?.vehicleNumber || '',
            // Include vehicles array for future use
            vehicles: values.vehicles || [],
            // License fields
            drivingLicenceNumber: values.drivingLicenceNumber || '',
            driverLicenceProof: values.driverLicenceProof || '',
            drivingLicenceExpiryDate: values.drivingLicenceExpiryDate || '',
          };
          try {
            if (deliveryBoyData) {
              EditDriverManager(
                {
                  id: deliveryBoyData.id || '',
                  data: modifiedValues,
                },
                {
                  onSuccess: () => {
                    onCancel();
                  },
                  onError: (error: any) => {
                    console.log(error);
                  },
                }
              );
            } else {
              CreateDriverManager(modifiedValues, {
                onSuccess: () => {
                  onCancel();
                },
                onError: (error: any) => {
                  console.error(error);
                },
              });
            }
          } catch (error) {
            console.error('Error submitting form:', error);
            // Optionally, you can show an error message to the user
          }
        },

        fields: [
          {
            name: 'firstName',
            label: 'First Name:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'lastName',
            label: 'Last Name:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'email',
            label: 'Email:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'mobileNumber',
            label: 'Mobile No:',
            type: 'custom',
            isRequired: true,
            component: ({ field, form }) => (
              <PhoneNumberInput
                field={field}
                form={form}
                selectedCountryCode={form.values.countryCode}
                onCountryCodeChange={code => form.setFieldValue('countryCode', code)}
              />
            )
          },
          {
            name: 'alternativeMobileNumber',
            label: 'Alternative Mobile No:',
            type: 'custom',
            isRequired: false,
            component: ({ field, form }) => (
              <PhoneNumberInput
                field={field}
                form={form}
                selectedCountryCode={form.values.alternativeCountryCode}
                onCountryCodeChange={code => form.setFieldValue('alternativeCountryCode', code)}
              />
            )
          },
          ...(!deliveryBoyData
            ? [
              {
                name: 'password',
                label: 'Password',
                type: 'password',
                isRequired: true,
              },
            ]
            : []),
          {
            name: 'currencyCode',
            label: 'Currency Code:',
            type: 'text',
            isRequired: true,
            value: 'JOD',
            disabled: true
          },
          {
            name: 'deliveryVehicleType',
            label: 'Delivery Vehicle Option:',
            type: 'custom',
            isRequired: true,
            component: ({ field, form }: any) => (
              <Box>
                <MultiSelectField
                  field={field}
                  form={form}
                  options={deliveryVehicleOptions}
                  placeholder="Select Vehicle Types"
                  width={400}
                />
                {form.touched[field.name] && form.errors[field.name] && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {form.errors[field.name]}
                  </FormHelperText>
                )}
              </Box>
            ),
          },
          {
            name: 'vehicles',
            label: 'Vehicle Details:',
            type: 'custom',
            isRequired: true,
            component: ({ form }: any) => {
              const selectedTypes = Array.isArray(form.values.deliveryVehicleType) 
                ? form.values.deliveryVehicleType 
                : [];
              
              if (selectedTypes.length === 0) {
                return (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Please select at least one vehicle type above
                  </Typography>
                );
              }
              
              return (
                <VehicleInputFields
                  form={form}
                  selectedVehicleTypes={selectedTypes}
                  country={form.values.country || ''}
                />
              );
            },
          },
          {
            name: 'maximumOrderLimit',
            label: 'Number of order limit:',
            type: 'number',
            isRequired: true,
          },
          {
            name: 'commonFare',
            label: 'Common Fare:',
            type: 'number',
            isRequired: true,
          },
          {
            name: 'responseTime',
            label: 'Response Time (minutes):',
            type: 'number',
            isRequired: true,
          },
          {
            name: '',
            type: 'section',
            sectionTitle: 'Address',
            sectionLine: true,
          },
          {
            name: 'location',
            label: 'Location:',
            type: 'custom',
            isRequired: true,
            component: ({ field, form }: any) => {
                             const handleSearchLocation = (data: {
                 latLng: { lat: number; lng: number };
                 city: string;
                 state: string;
                 country: string;
               }) => {
                 // Determine city value: use city if available, otherwise use state, otherwise use country
                 const cityValue = data.city && data.city.trim() 
                   ? data.city 
                   : (data.state && data.state.trim() ? data.state : data.country);
                 
                 // Determine state value: use state if available, otherwise use country
                 const stateValue = data.state && data.state.trim() ? data.state : data.country;
                 
                 // Create location string based on available data
                 let locationString;
                 if (data.city && data.city.trim() && data.state && data.state.trim()) {
                   // Full data available: City, State, Country
                   locationString = `${data.city}, ${data.state}, ${data.country}`;
                 } else if (data.state && data.state.trim()) {
                   // Only state available: State, Country (state will be used as city)
                   locationString = `${data.state}, ${data.country}`;
                 } else {
                   // Only country available: Country
                   locationString = data.country;
                 }
                 
                 form.setFieldValue(field.name, locationString);
                 form.setFieldTouched(field.name, false);
                 form.setFieldValue('city', cityValue);
                 form.setFieldTouched('city', false);
                 form.setFieldValue('state', stateValue);
                 form.setFieldTouched('state', false);
                 form.setFieldValue('country', data.country);
                 form.setFieldTouched('country', false);
               };

              const hasError =
                form.touched[field.name] && Boolean(form.errors[field.name]);

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <LocationSearchInput
                    key={inputKey}
                    handlesearchLocation={handleSearchLocation}
                    address={address}
                    setAddress={(val) => {
                      setAddress(val);
                      form.setFieldValue(field.name, val);
                      form.setFieldTouched(field.name, true, false);
                    }}
                  />
                  {hasError && (
                    <FormHelperText error sx={{ mt: 0.5, mb: 0 }}>
                      {form.errors[field.name]}
                    </FormHelperText>
                  )}
                </Box>
              );
            },
          },
          {
            name: 'country',
            label: 'Country:',
            type: 'text',
            isRequired: true,
            disabled: true,
          },
          {
            name: 'state',
            label: 'State:',
            type: 'text',
            isRequired: true,
            disabled: true,
          },
          {
            name: 'city',
            label: 'City:',
            type: 'text',
            isRequired: true,
            disabled: true,
          },
          {
            name: '',
            type: 'section',
            sectionTitle: 'Driving License Information',
            sectionLine: true,
          },
          {
            name: 'drivingLicenceNumber',
            label: 'Driving Licence Number:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'driverLicenceProof',
            label: 'Driving Licence Proof:',
            type: 'custom',
            isRequired: true,
            component: ({ field, form }: any) => (
              <Box sx={{ width: 400 }}>
                <ImageUploadField
                  name={field.name}
                  label="Driving Licence Proof"
                  multiple={false}
                  maxFiles={1}
                />
                {form.touched[field.name] && form.errors[field.name] && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {form.errors[field.name]}
                  </FormHelperText>
                )}
              </Box>
            ),
          },
          {
            name: 'drivingLicenceExpiryDate',
            label: 'Driving Licence Expiry Date:',
            type: 'custom',
            isRequired: true,
            component: ({ field, form }: any) => {
              const error = form.touched[field.name] && form.errors[field.name];
              return (
                <Box sx={{ width: 400 }}>
                  <DatePicker
                   // label="Expiry Date"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      form.setFieldValue(
                        field.name,
                        newValue ? newValue.format('YYYY-MM-DD') : ''
                      );
                    }}
                    minDate={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(error),
                        helperText: (
                          <Box sx={{ minHeight: '20px' }}>
                            {error ? form.errors[field.name] : ''}
                          </Box>
                        ),
                      },
                      popper: {
                        sx: {
                          zIndex: 1400,
                        },
                        placement: 'bottom-start',
                        modifiers: [
                          {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                              altAxis: true,
                              altBoundary: true,
                              tether: true,
                              rootBoundary: 'viewport',
                            },
                          },
                        ],
                      },
                    }}
                  />
                </Box>
              );
            },
          },
        ],

        customButton: ({ resetForm, handleSubmit }) => (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: 3,
                py: 1,
                backgroundColor: COLORS.primary.main,
                '&:hover': {
                  backgroundColor: COLORS.primary.dark,
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              onClick={(e) => {
                // Ensure we go through Formik's submit pipeline explicitly
                e.preventDefault();
                console.log('Create button clicked, calling Formik handleSubmit');
                handleSubmit();
              }}
              disabled={CreateDriverManagerLoading || EditDriverManagerLoading}
            >
              {CreateDriverManagerLoading || EditDriverManagerLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                  {deliveryBoyData ? 'Updating...' : 'Creating...'}
                </>
              ) : deliveryBoyData ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>

            {!deliveryBoyData && (
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  resetForm();
                  setAddress('');
                  setInputKey((prev) => prev + 1);
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  borderColor: COLORS.secondary.main,
                  color: COLORS.secondary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                Reset
              </Button>
            )}
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onCancel}
              sx={{
                bgcolor: warning.main,
                color: '#fff',
                '&:hover': {
                  bgcolor: warning.dark,
                  color: '#fff',
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        ),
      })()}
    </>
  );
};

export default AddDriver;
